/* client/src/components/Admin/Materials.tsx */
import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Box,
  Chip,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { apiGetMaterialTypes } from '../../services/apiService';
import { useMaterials } from '../../contexts/MaterialsContext';

interface MaterialType {
  id: number;
  name: string;
  unit: string;
}

// Use the same Material interface as the context by importing it or redefining consistently
interface Material {
  id: number;
  name: string;
  unit_price: number;
  unit: string;
  description?: string; // Make description optional to match context
  type_id?: number;
  type_name?: string;
  is_active: boolean;
  created_at: string;
}

function Materials() {
  const { materials, loading, error, addMaterial, updateMaterial, deleteMaterial } = useMaterials();
  
  const [materialTypes, setMaterialTypes] = useState<MaterialType[]>([]);
  const [open, setOpen] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState<Material | null>(null);
  const [localError, setLocalError] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    unit_price: '',
    unit: 'grams',
    description: '',
    type_id: '',
    is_active: true,
  });

  useEffect(() => {
    fetchMaterialTypes();
  }, []);

  const fetchMaterialTypes = async () => {
    try {
      const data = await apiGetMaterialTypes();
      setMaterialTypes(data);
    } catch (err: any) {
      console.error('Failed to fetch material types:', err);
      setLocalError('Failed to load material types');
    }
  };

  const handleOpen = (material: Material | null = null) => {
    if (material) {
      setEditingMaterial(material);
      setFormData({
        name: material.name,
        unit_price: material.unit_price.toString(),
        unit: material.unit,
        description: material.description || '', // Handle undefined description
        type_id: material.type_id?.toString() || '',
        is_active: material.is_active,
      });
    } else {
      setEditingMaterial(null);
      setFormData({
        name: '',
        unit_price: '',
        unit: 'grams',
        description: '',
        type_id: '',
        is_active: true,
      });
    }
    setOpen(true);
    setLocalError('');
  };

  const handleClose = () => {
    setOpen(false);
    setEditingMaterial(null);
    setLocalError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const materialData = {
        ...formData,
        unit_price: parseFloat(formData.unit_price),
        type_id: formData.type_id ? parseInt(formData.type_id) : null,
      };

      if (editingMaterial) {
        await updateMaterial(editingMaterial.id, materialData);
      } else {
        await addMaterial(materialData);
      }
      
      handleClose();
    } catch (err: any) {
      setLocalError(err.message || `Failed to ${editingMaterial ? 'update' : 'add'} material`);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this material?')) {
      try {
        await deleteMaterial(id);
      } catch (err: any) {
        setLocalError(err.message || 'Failed to delete material');
      }
    }
  };

  const formatPrice = (price: number | string): string => {
    const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
    return `د.${numericPrice.toFixed(2)}`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Materials Management</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
          Add Material
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => {}}>
          {error}
        </Alert>
      )}

      {localError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLocalError('')}>
          {localError}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right">Unit Price</TableCell>
              <TableCell>Unit</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {materials.map((material) => (
              <TableRow key={material.id}>
                <TableCell>{material.name}</TableCell>
                <TableCell>{material.type_name || 'No Type'}</TableCell>
                <TableCell align="right">{formatPrice(material.unit_price)}</TableCell>
                <TableCell>{material.unit}</TableCell>
                <TableCell>{material.description || '-'}</TableCell> {/* Handle undefined description */}
                <TableCell>
                  <Chip 
                    label={material.is_active ? 'Active' : 'Inactive'} 
                    color={material.is_active ? 'success' : 'error'} 
                    size="small" 
                  />
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleOpen(material)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(material.id)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {materials.length === 0 && !loading && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="textSecondary">No materials found</Typography>
          </Box>
        )}
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingMaterial ? 'Edit Material' : 'Add New Material'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              margin="dense"
              name="name"
              label="Name"
              fullWidth
              variant="outlined"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              required
            />
            <TextField
              margin="dense"
              name="type_id"
              label="Material Type"
              select
              fullWidth
              variant="outlined"
              value={formData.type_id}
              onChange={(e) => setFormData({...formData, type_id: e.target.value})}
            >
              <MenuItem value="">No Type</MenuItem>
              {materialTypes.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  {type.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              margin="dense"
              name="unit_price"
              label="Unit Price"
              type="number"
              fullWidth
              variant="outlined"
              value={formData.unit_price}
              onChange={(e) => setFormData({...formData, unit_price: e.target.value})}
              inputProps={{ step: "0.01", min: "0" }}
              required
            />
            <TextField
              margin="dense"
              name="unit"
              label="Unit"
              select
              fullWidth
              variant="outlined"
              value={formData.unit}
              onChange={(e) => setFormData({...formData, unit: e.target.value})}
              required
            >
              <MenuItem value="grams">Grams</MenuItem>
              <MenuItem value="kilograms">Kilograms</MenuItem>
              <MenuItem value="ounces">Ounces</MenuItem>
            </TextField>
            <TextField
              margin="dense"
              name="description"
              label="Description"
              fullWidth
              variant="outlined"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              multiline
              rows={3}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingMaterial ? 'Update' : 'Add'} Material
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}

export default Materials;